import React, { useState, useEffect, useRef } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label, Form, Alert, Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useStripe } from '@stripe/react-stripe-js';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { createTip, confirmBlikPayment, resetTipState } from '../../slices/tip/thunk';
import { notificationService } from '../../helpers/NotificationService';
import { authUserManager } from '../../helpers/api_helper';

const TipHostModal = ({ isOpen, toggle, eventId, adminUsername, minAmount = 2, guestName: guestNameProp, onSuccess, pendingAction }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const dispatch = useDispatch();
  const guest = authUserManager.getGuest();
  const guestName = guestNameProp || guest?.username || 'Anonymous';
  const [amount, setAmount] = useState('');
  const [blikCode, setBlikCode] = useState('');
  const [validationError, setValidationError] = useState(null);
  const confirmationAttempted = useRef(false); // Zapobiega wielokrotnym próbom

  // Redux selector
  const selectTipState = (state) => state.Tip;
  const tipSelector = createSelector(selectTipState, (tip) => ({
    loading: tip.loading,
    success: tip.success,
    error: tip.error,
    clientSecret: tip.clientSecret,
    retryCount: tip.retryCount,
    isRetrying: tip.isRetrying,
  }));

  const { loading, success, error, clientSecret, retryCount, isRetrying } = useSelector(tipSelector);

  // Reset state przy zamykaniu modala
  useEffect(() => {
    if (!isOpen) {
      dispatch(resetTipState());
      setBlikCode('');
      setAmount('');
      setValidationError(null);
      confirmationAttempted.current = false;
    } else if (minAmount > 2) {
      // Wymagany napiwek: podpowiedz minimalną kwotę
      setAmount(String(minAmount));
    }
  }, [isOpen, dispatch, minAmount]);

  // Auto-potwierdzenie gdy mamy clientSecret i kod BLIK (tylko raz!)
  useEffect(() => {
    if (
      clientSecret &&
      blikCode &&
      stripe &&
      !loading &&
      !success &&
      !confirmationAttempted.current
    ) {
      confirmationAttempted.current = true;
      dispatch(confirmBlikPayment(stripe, clientSecret, blikCode, amount, guestName, eventId));
    }
  }, [clientSecret, blikCode, stripe, dispatch, loading, success, amount, guestName, eventId]);

  // Sukces - zamknij modal i pokaż powiadomienie
  useEffect(() => {
    if (success) {
      // Username jest opcjonalny — bez niego ogólne podziękowanie.
      notificationService.success(adminUsername
        ? adminUsername + ' ' + t('thanks you for the tip') + '! :)'
        : t('Thank you for your tip!') + ' :)');
      dispatch(resetTipState());
      if (onSuccess) {
        onSuccess();
      }
      // Bez zwłoki: gość i tak zobaczy tę informację dopiero po powrocie z aplikacji
      // banku, a wtedy chce zobaczyć listę, nie okienko płatności. Podziękowanie
      // zostaje w tostcie powyżej.
      toggle();
    }
  }, [success, toggle, t, adminUsername, dispatch, onSuccess]);

  const [amountError, setAmountError] = useState(null);

  const validateAmount = (value) => {
    const numericAmount = parseFloat(value.replace(',', '.'));

    if (isNaN(numericAmount) || value === '') {
      setAmountError(t('Please enter amount'));
      return false;
    }
    if (numericAmount < minAmount) {
      setAmountError(t('Minimum amount is') + ' ' + minAmount + ' PLN');
      return false;
    }
    if (numericAmount > 500) {
      setAmountError(t('Maximum amount is 500 PLN'));
      return false;
    }

    setAmountError(null);
    return true;
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setAmount(value);
    validateAmount(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError(null);
    confirmationAttempted.current = false; // Reset flagi

    const numericAmount = parseInt(amount, 10);

    if (isNaN(numericAmount) || numericAmount < minAmount) {
      setValidationError(t('Minimum tip amount is') + ' ' + minAmount + ' PLN');
      return;
    }

    if (numericAmount > 500) {
      setValidationError(t('Maximum tip amount is 500 PLN'));
      return;
    }

    // Walidacja kodu BLIK
    if (!/^\d{6}$/.test(blikCode)) {
      setValidationError(t('Please enter a valid 6-digit BLIK code'));
      return;
    }

    if (!stripe) {
      setValidationError(t('Payment system not ready. Please try again.'));
      return;
    }

    // Utwórz płatność (kwota w groszach). pendingAction mówi serwerowi, co dodać
    // po zaksięgowaniu napiwku — bez tego utwór czekałby na powrót do przeglądarki.
    dispatch(createTip(
      eventId,
      Math.round(numericAmount * 100),
      guestName,
      pendingAction
    ));
  };

  const getErrorMessage = () => {
    if (validationError) return validationError;
    if (error) {
      if (typeof error === 'string') return error;
      return t('An error occurred. Please try again.');
    }
    return null;
  };

  const getLoadingMessage = () => {
    if (isRetrying) {
      return `${t('Retry attempt')} ${retryCount}/3... ${t('Please confirm payment in your banking app.')}`;
    }
    if (clientSecret) {
      return t('Confirming payment... Please approve in your banking app.');
    }
    return t('Creating payment...');
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <Form onSubmit={handleSubmit} noValidate>
        <ModalHeader toggle={toggle}>
          {t('Leave a tip for the host')}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Label htmlFor="tipAmount">
              {t('Amount (PLN)')} <span className="text-danger">*</span>
            </Label>
            <Input
              id="tipAmount"
              type="number"
              min={minAmount}
              max="500"
              step="1"
              value={amount}
              onKeyPress={(e) => !/[0-9]/.test(e.key) && e.preventDefault()}
              onChange={handleAmountChange}
              onBlur={() => validateAmount(amount)}
              disabled={loading}
              placeholder={t('Enter amount')}
              invalid={!!amountError}
            />
            {amountError && (
              <div className="invalid-feedback d-block">
                {amountError}
              </div>
            )}
            <small className="text-muted">
              {t('Minimum')}: {minAmount} PLN, {t('Maximum')}: 500 PLN
            </small>
          </div>

          <div className="mb-3">
            <Label htmlFor="blikCode">
              {t('BLIK code')} <span className="text-danger">*</span>
            </Label>
            <Input
              id="blikCode"
              type="text"
              maxLength="6"
              value={blikCode}
              onChange={(e) => setBlikCode(e.target.value.replace(/\D/g, ''))}
              placeholder={t('Enter 6-digit BLIK code')}
              disabled={loading}
              className="text-center fs-6 fw-bold"
              style={{ letterSpacing: '0.1em' }}
            />
            <small className="text-muted">
              <i className="ri-smartphone-line me-1"></i>
              {t('Open your banking app, generate a BLIK code and enter it here.')}
            </small>
          </div>

          {getErrorMessage() && (
            <Alert color="danger" className="mb-3">
              <i className="ri-error-warning-line me-2"></i>
              {getErrorMessage()}
            </Alert>
          )}

          {loading && (
            <Alert color="info" className="mb-3">
              <div className="d-flex align-items-center">
                <Spinner size="sm" className="me-2" />
                <div>
                  <div>{getLoadingMessage()}</div>
                  {isRetrying && (
                    <small className="text-muted">
                      {t('If you dont see a notification, generate a new BLIK code.')}
                    </small>
                  )}
                </div>
              </div>
            </Alert>
          )}

          {success && (
            <Alert color="success" className="mb-3">
              <i className="ri-checkbox-circle-line me-2"></i>
              {t('Payment successful! Thank you for your tip!')}
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            className="btn bg-transparent border-0 shadow-none color-primary d-inline-flex align-items-center px-2 me-2"
            onClick={toggle}
            disabled={loading}
          >
            <i className="ri-close-circle-line align-middle me-2" style={{ fontSize: '1.6rem' }}></i>
            {t('Close')}
          </Button>

          <Button
            type="submit"
            className="btn bg-transparent border-0 shadow-none color-primary d-inline-flex align-items-center px-2"
            disabled={loading || !stripe || success}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                {t('Processing...')}
              </>
            ) : success ? (
              <>
                <i className="ri-checkbox-circle-line align-middle me-2" style={{ fontSize: '1.6rem' }}></i>
                {t('Completed')}
              </>
            ) : (
              <>
                <i className="ri-bank-card-line align-middle me-2" style={{ fontSize: '1.6rem' }}></i>
                {t('Pay with BLIK')}
              </>
            )}
          </Button>

        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default TipHostModal;
